import { State } from '@meraki-internal/state';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CareMapState, ICareMapOnboardingStep } from '../caremap/CareMapState';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { NewCareMapWizard } from '../wizard/mini-wizard/wizards/NewCareMapWizard';
import { OnboardCareRecipientWizard } from '../wizard/mini-wizard/wizards/OnboardCareRecipientWizard';
import { EditPatientDrawer } from '../household/patient/EditPatientDrawer';
import { AuthHandler } from '../auth/AuthHandler';
import { ISupportGroupKey } from '../caremap/ICareMap';
import dayjs from 'dayjs';
import { AppointmentEditorDrawer } from '../appointments/AppointmentEditorDrawer';
import { AppointmentsState, NEW_APPOINTMENT_ID } from '../appointments/AppointmentsState';
import { AddNoteWithQueryParamDrawer } from '../notes/add-note/AddNoteWithQueryParamDrawer';
import { CheckinState } from '../journal/model/CheckinState';
import { initCap } from '../utils/format';
import { IUserOnboardingStep, UserOnboardingState } from './UserOnboardingState';
import { ChecklistState, FINANCIAL_CHECKLIST_SEQUENCE_MAP, IFinancialChecklist } from '../checklists/ChecklistState';
import { IOnboardingTask } from './IOnBoardingTask';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { HelperBeeButton } from '../helper-bee/HelperBeeButton';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';

export class OnboardingModel extends State {

    static inject = () => [
        AppPrincipal,
        CareMapState,
        CaremapNotesModelFactory,
        NewCareMapWizard,
        OnboardCareRecipientWizard,
        EditPatientDrawer,
        AppointmentEditorDrawer,
        AddNoteWithQueryParamDrawer,
        AuthHandler,
        HistoryViewModel,
        AppointmentsState,
        CheckinState,
        UserOnboardingState,
        ChecklistState,
        MixPanelEventEmitter
    ];
    constructor(
        private principal: AppPrincipal,
        private careMapState: CareMapState,
        private notesFactory: CaremapNotesModelFactory,
        private wizard1: NewCareMapWizard,
        private wizard2: OnboardCareRecipientWizard,
        private editPatientDrawer: EditPatientDrawer,
        private addApptDrawer: AppointmentEditorDrawer,
        private addNoteDrawer: AddNoteWithQueryParamDrawer,
        private authHandler: AuthHandler,
        private history: HistoryViewModel,
        private appointmentState: AppointmentsState,
        private checkinState: CheckinState,
        private userOnboardingState: UserOnboardingState,
        private checklistState: ChecklistState,
        private tracking: MixPanelEventEmitter
    ) {
        super({});

        careMapState.subscribe(() => {
            this.setState({});
        });

        notesFactory.subscribe(() => {
            this.setState({});
        });

        appointmentState.subscribe(() => {
            this.setState({});
        });

        checkinState.subscribe(() => {
            this.setState({});
        });

        userOnboardingState.subscribe(() => {
            this.setState({});
        });
    };

    getTasks = (): IOnboardingTask[] => {
        const careMap = this.careMapState.getActiveCaremap();
        const hasCareMap = Boolean(careMap);
        const canEdit = this.careMapState.hasPermissionTo('put');

        const notesModel = hasCareMap ? this.notesFactory.forActiveCaremap() : undefined;
        const notesLoaded = Boolean(notesModel && notesModel.hasLoaded);
        const hasAttachment = Boolean(notesModel && notesLoaded && notesModel.getNotes().some(n => n.attachments.length > 0));
        const hasPost = Boolean(notesModel && notesModel.getNotes().filter(n => !n.isPrivate).length > 0);
        const hasAppts = Boolean(this.appointmentState.state.loaded && this.appointmentState.state.appointments.length);
        const hasCheckins = Boolean(this.checkinState.getPersonalCheckins().length);

        const firstName = careMap?.patient?.firstName;
        const self = Boolean(careMap?.initialWizardAnswers['recipient-self']);
        const namePart = firstName && !self ? `${firstName}'s` : 'your';

        const isAnonymous = isAnonymousViewer(this.principal);
        const isCareMapIncomplete = !careMap || this.careMapState.isIncomplete(careMap);
        const isAddingRecipient = this.careMapState.isInOnboardingStep('add-care-recipient');
        const isPatientIncomplete = !hasCareMap || this.careMapState.isPatientIncomplete();

        const hasOpenedBea = this.userOnboardingState.isStepComplete('opened-bea');
        const isGettingStartedComplete = hasCareMap && !isAddingRecipient && !isPatientIncomplete && hasOpenedBea;

        if (hasCareMap && !canEdit) {
            // TODO: create cards for viewer (for now, show nothing)
            return [];
        }

        const addPatient = async () => {
            // if it's auto created (ie recorded an appt) the user hasn't filled information out
            if (!careMap?.careMapId || !careMap.essentialMapComplete) {
                this.wizard1.start();
            } else {
                this.wizard2.start(careMap.careMapId);
            }
        };

        const editPatient = async () => {
            this.editPatientDrawer.open();
        };

        const getPatientAgeInYears = () => {
            const today = dayjs();
            const dayjsDob = dayjs(careMap?.patient.dob);
            const years = today.diff(dayjsDob, 'years');

            return years ? years : 0;
        };

        const hasHive = (hive: ISupportGroupKey) => {
            return hasCareMap && Boolean(this.careMapState.getSupportGroup(hive).isRelevant);
        };

        const gotoCommunity = () => {
            this.history.push(`/community/${careMap!.careMapId}/feed`);
        };

        const navigateToHousehold = () => {
            this.history.push(`/caremaps/${careMap!.careMapId}/drawer/household`);
        };

        const navigateToSupportGroup = (supportGroupKey: ISupportGroupKey) => {
            this.history.push(`/caremaps/${careMap!.careMapId}/drawer/support-group/${supportGroupKey}`);
        };

        const addSupportGroup = (supportGroupKey: ISupportGroupKey) => {
            this.history.push(`/caremaps/${careMap!.careMapId}/drawer/support-group/${supportGroupKey}/add`);
        };

        const addAppointment = async () => {
            this.addApptDrawer.open({ appointmentId: NEW_APPOINTMENT_ID });
        };

        const viewAllAppointments = () => {
            this.history.push(`/calendar/appointments/${careMap!.careMapId}`);
        };

        const addNote = async () => {
            this.addNoteDrawer.open({ careMapOnboardingStepComplete: 'add-note' });
        };

        const viewAllNotes = () => {
            this.history.push(`/notes/${careMap!.careMapId}`);
        };

        const addDocument = async () => {
            this.addNoteDrawer.open({autoOpenFileChooser: true});
        };

        const hasCompletedCareMapStep = (step: ICareMapOnboardingStep) => {
            return hasCareMap && this.careMapState.hasCompletedOnboardingStep(step);
        };

        const hasCompletedUserStep = (step: IUserOnboardingStep) => {
            return this.userOnboardingState.isStepComplete(step);
        };

        const completeUserStep = (step: IUserOnboardingStep, action: () => void) => {
            action();
            this.userOnboardingState.completeStep(step);
        };

        const openFinancialChecklist = (list: IFinancialChecklist) => {
            this.checklistState.setAutoExpandInfo({
                collapseOthers: true,
                expandIndex: FINANCIAL_CHECKLIST_SEQUENCE_MAP[list]
            });

            navigateToSupportGroup('financial');
        };

        const goToJournal = () => {
            this.tracking.track('Daily Log Calendar Opened', () => ({'Opened From': 'Home Page'}));

            if (!this.userOnboardingState.isStepComplete('viewed-journal-summary')) {
                this.userOnboardingState.completeStep('viewed-journal-summary');
            }

            this.history.push('/calendar/journal');
        };

        const createAccount = async () => {
            await this.authHandler.login({ trackingFrom: 'Setup' });
        };

        return [
            {
                heading: 'Get Started...',
                steps: [
                    {
                        heading: 'Create your account',
                        text: 'Welcome to the Hive! Sign in to record care notes and unlock access to more tools, resources, and community features.',
                        todoBtn: {
                            label: 'Create Account',
                            action: createAccount
                        },
                        ready: true,
                        completed: !isAnonymous
                    },
                    {
                        heading: 'Add a care recipient',
                        text: 'Enter basic information like name, age, and relationship to you. This is the foundation of your care plan.',
                        todoBtn: {
                            label: isAddingRecipient && !isCareMapIncomplete ? 'Continue Adding Care Recipient' : 'Add Care Recipient',
                            action: addPatient
                        },
                        ready: !isAnonymous,
                        completed: hasCareMap && !isAddingRecipient
                    },
                    {
                        heading: `Complete ${self ? 'your' : 'their'} care profile`,
                        text: `Fill out critical details such as medical conditions, allergies, and contact info. The more complete the profile, the easier it will be to manage ${self ? 'your' : 'their'} care needs moving forward!`,
                        todoBtn: {
                            label: 'Complete Profile',
                            action: editPatient
                        },
                        doneBtn: {
                            label: 'View Profile',
                            action: () => this.history.push(`/care/${careMap!.careMapId}`)
                        },
                        ready: hasCareMap && !isAddingRecipient && isPatientIncomplete,
                        completed: !isPatientIncomplete
                    },
                    {
                        heading: 'Get Answers',
                        text: 'Have a burning question? Bea, your AI-powered caregiving consultant, provides quick answers and guidance to support you every step of the way.',
                        video: {
                            id: 'get-answers-video',
                            thumbnail: '/assets/images/onboarding/bea-video-thumbnail.png',
                            movie: '/assets/videos/onboarding/bea-chat-demo.mp4'
                        },
                        todoBtn: {
                            label: (
                                <div style={{margin: '20px auto', maxWidth: 300, textAlign: 'center', display: 'flex', justifyContent: 'center'}}>
                                    <HelperBeeButton
                                        label="Ask Bea for help or advice!"
                                        trackingFrom="Onboarding To Do"
                                    />
                                </div>
                            ),
                            action: () => console.log('Open Bea. Consolidate from HelperBeeButton?')
                        },
                        doneBtn: {
                            label: (
                                <div style={{margin: '20px auto', maxWidth: 300, textAlign: 'center', display: 'flex', justifyContent: 'center'}}>
                                    <HelperBeeButton
                                        data-id="helperbee-task-complete"
                                        label="Chat with Bea, she can help!"
                                        trackingFrom="Onboarding Revisited"
                                    />
                                </div>
                            ),
                            action: () => console.log('Open Bea. Consolidate from HelperBeeButton?')
                        },
                        ready: hasCareMap && !isAddingRecipient,
                        completed: hasOpenedBea
                    }
                ]
            },
            {
                heading: 'Grow Your Community!',
                text: `Caregiving is a team effort. Building your community is essential for sharing updates, coordinating tasks, and getting emotional support from family, friends, and professionals.`,
                steps: [
                    {
                        heading: 'Share your story',
                        text: `Add an introductory post about ${namePart} current care situation to welcome and connect with your Innerhive community!`,
                        todoBtn: {
                            label: 'Write Post',
                            action: gotoCommunity
                        },
                        doneBtn: {
                            label: 'Go to Community',
                            action: gotoCommunity
                        },
                        ready: hasCareMap && !isAddingRecipient && notesLoaded,
                        completed: hasPost
                    },
                    {
                        heading: `Add ${namePart} Inner Circle`,
                        text: `Add ${namePart} closest family members and primary caregivers to the Inner Circle of the Care Map- this also invites them to join your Innerhive community!`,
                        todoBtn: {
                            label: 'Add Inner Circle Members',
                            action: navigateToHousehold
                        },
                        doneBtn: {
                            label: 'View Inner Circle',
                            action: navigateToHousehold
                        },
                        ready: hasCareMap && !isAddingRecipient,
                        completed: hasCompletedCareMapStep('household-members'),
                    }
                ]
            },
            {
                heading: `Visualize ${initCap(namePart)} Care`,
                text: `Let’s build a Care Map! A Care Map is a visual overview of all the people and places involved in ${namePart} care. In Innerhive, it also serves as an intuitive place to organize and store care team details, including essential contact info. Caregivers love how it simplifies care coordination and fosters understanding, while keeping everything easily accessible.`,
                image: () => {
                    const age = getPatientAgeInYears();

                    if (careMap?.patient.dob && age <= 25) {
                        //age 0-16: Eleanor
                        return '/assets/images/onboarding/caremap-eleanor.png';
                    } else if (age > 25 && age <= 50) {
                        //age 17-50: Samantha
                        return '/assets/images/onboarding/caremap-samantha.png';
                    }

                    // age 51+: Arthur
                    return '/assets/images/onboarding/caremap-arthur.png';
                },
                steps: [
                    {
                        heading: 'Add Primary Care hive',
                        text: `Start by adding ${namePart} primary care provider or another key medical professional involved in ${self ? 'your' : 'their'} care. This will ensure you have quick access to their contact details and care insights when needed.`,
                        todoBtn: {
                            label: 'Add Primary Care',
                            action: () => addSupportGroup('medical')
                        },
                        doneBtn: {
                            label: 'Open Primary Care hive',
                            action: () => navigateToSupportGroup('medical')
                        },
                        image: () => '/assets/images/onboarding/primary-care-hive.png',
                        ready: isGettingStartedComplete,
                        completed: hasHive('medical')
                    },
                    {
                        heading: 'Add Specialists hive',
                        text: `Add one of ${namePart} specialists, such as a dentist, so you can capture and organize details related to a specific healthcare focus.`,
                        todoBtn: {
                            label: 'Add Specialists',
                            action: () => addSupportGroup('specialists')
                        },
                        doneBtn: {
                            label: 'Open Specialists hive',
                            action: () => navigateToSupportGroup('specialists')
                        },
                        image: () => '/assets/images/onboarding/specialists-hive.png',
                        ready: isGettingStartedComplete,
                        completed: hasHive('specialists')
                    },
                    {
                        heading: 'Add Living hive',
                        text: `Store essential household and living-related details in ${namePart} Living hive. This is the best place to store info for living facilities, support staff, utility services, meal delivery programs, home devices like internet information, and more to help streamline day-to-day care.`,
                        todoBtn: {
                            label: 'Add Living hive',
                            action: () => addSupportGroup('social')
                        },
                        doneBtn: {
                            label: 'Open Living hive',
                            action: () => navigateToSupportGroup('social')
                        },
                        image: () => '/assets/images/onboarding/living-hive.png',
                        ready: isGettingStartedComplete,
                        completed: hasHive('social')
                    },
                    {
                        heading: 'Add Community hive',
                        text: `Include a member of ${namePart} broader support network, such as a neighbor, friend, or group, that provides occasional care and support. This ensures that everyone contributing to ${namePart} well-being is represented on the map.`,
                        todoBtn: {
                            label: 'Add Community hive',
                            action: () => addSupportGroup('community')
                        },
                        doneBtn: {
                            label: 'Open Community hive',
                            action: () => navigateToSupportGroup('community')
                        },
                        image: () => '/assets/images/onboarding/community-hive.png',
                        ready: isGettingStartedComplete,
                        completed: hasHive('community')
                    },
                    {
                        heading: 'Add School hive',
                        text: `Add a key member of ${namePart} school team, like a teacher, counselor, or administrator. This helps you keep track of those who support ${namePart} learning and development in school settings.`,
                        todoBtn: {
                            label: 'Add School hive',
                            action: () => addSupportGroup('school')
                        },
                        doneBtn: {
                            label: 'Open School hive',
                            action: () => navigateToSupportGroup('school')
                        },
                        image: () => '/assets/images/onboarding/school-hive.png',
                        hidden: getPatientAgeInYears() >= 25,
                        ready: isGettingStartedComplete,
                        completed: hasHive('school')
                    }
                ]
            },
            {
                heading: `Organize ${initCap(namePart)} Care`,
                text: 'Say goodbye to cluttered binders and scattered notes! Make the most of these Innerhive features that keep all of your caregiving information in one secure, accessible place. Organized caregivers experience less stress and reduced anxiety!',
                steps: [
                    {
                        heading: 'Track an upcoming appointment',
                        text: `Log ${namePart} next appointment and discover how Innerhive+ streamlines managing your caregiving schedule. You can add key details, sync to your calendar of choice, and separate care responsibilities so you always know what’s on deck!`,
                        todoBtn: {
                            label: 'Add Appointment',
                            action: addAppointment
                        },
                        doneBtn: {
                            label: 'View All Appointments',
                            action: viewAllAppointments
                        },
                        ready: isGettingStartedComplete,
                        completed: hasAppts
                    },
                    {
                        heading: 'Add your first note',
                        text: `Capture any caregiving insights or questions you have for ${namePart} next appointment. With Innerhive, notes are always accessible and can never be lost allowing you to view and share the details that matter most!`,
                        todoBtn: {
                            label: 'Add Note',
                            action: addNote
                        },
                        doneBtn: {
                            label: 'View All Notes',
                            action: viewAllNotes
                        },
                        ready: isGettingStartedComplete,
                        completed: this.careMapState.hasCompletedOnboardingStep('add-note')
                    },
                    {
                        heading: 'Upload a care document',
                        text: `Store your first document, like a doctor’s note, medication list, or even one of ${namePart} favorite recipes. Keeping everything in one place means well-rounded care is always within reach!`,
                        todoBtn: {
                            label: 'Add Document',
                            action: addDocument
                        },
                        doneBtn: {
                            label: 'View All Notes',
                            action: viewAllNotes
                        },
                        ready: isGettingStartedComplete,
                        completed: hasAttachment
                    }
                ]
            },
            {
                heading: 'Find Balance',
                text: 'Healthy caregiving starts with healthy caregivers. Innerhive supports you with tools and resources to fight burnout and maintain balance, so you can provide the best care possible while prioritizing your own wellness too.',
                steps: [
                    {
                        heading: 'Add your first Care Journal entry',
                        text: 'Reflect on your caregiving journey and how you’re feeling today. A quick daily journal helps caregivers process emotions, track patterns, and prioritize wellness needs.',
                        todoBtn: {
                            label: 'Add Journal Entry',
                            action: () => this.history.push(`/calendar/journal/daily-log`)
                        },
                        doneBtn: {
                            label: 'View All Entries',
                            action: goToJournal
                        },
                        ready: isGettingStartedComplete,
                        completed: hasCheckins
                    },
                    {
                        heading: 'Access your Care Journal summary',
                        text: 'Take a look at your Care Journal summary to monitor trends that may be affecting your balance. Use this view to fill in any days you may have missed and gain deeper insights into your well-being over time.',
                        todoBtn: {
                            label: 'Open Journal Summary',
                            action: goToJournal
                        },
                        doneBtn: {
                            label: 'Open Journal Summary',
                            action: goToJournal
                        },
                        ready: isGettingStartedComplete && hasCheckins,
                        completed: hasCompletedUserStep('viewed-journal-summary')
                    },
                    {
                        heading: 'Explore curated support groups, services, and resources',
                        text: 'Discover services, support groups, and organizations to meet your unique caregiving needs. Innerhive connects you with trusted resources to make caregiving easier and less isolating.',
                        todoBtn: {
                            label: 'View Resources',
                            action: () => completeUserStep(
                                'viewed-resources',
                                () => this.history.push(`/resources`)
                            )
                        },
                        doneBtn: {
                            label: 'View Resources',
                            action: () => completeUserStep(
                                'viewed-resources',
                                () => this.history.push(`/resources`)
                            )
                        },
                        ready: isGettingStartedComplete,
                        completed: hasCompletedUserStep('viewed-resources')
                    }
                ]
            },
            {
                heading: 'Prepare for the Future',
                text: `Planning for the future can feel overwhelming, but Innerhive is here to help. From organizing crucial documents to navigating difficult conversations, our guided checklists provide clarity and structure to help you confidently navigate and protect ${namePart} future.`,
                steps: [
                    {
                        heading: 'Add Financial & Legal hive',
                        text: 'Organize critical details and contacts for trusted advisors for legal and financial matters. This is the first step in future-proofing your care plan!',
                        todoBtn: {
                            label: 'Add Financial & Legal hive',
                            action: () => addSupportGroup('financial')
                        },
                        doneBtn: {
                            label: 'Open Financial & Legal hive',
                            action: () => navigateToSupportGroup('financial')
                        },
                        image: () => '/assets/images/onboarding/financial-legal-hive.png',
                        ready: isGettingStartedComplete,
                        completed: hasHive('financial')
                    },
                    {
                        heading: 'Start Financial Planning',
                        text: 'Follow these first steps to organize immediate financial needs and build a foundation for preparing for the future.',
                        todoBtn: {
                            label: 'Start Planning',
                            action: () => openFinancialChecklist('getting-started')
                        },
                        doneBtn: {
                            label: 'Open Checklist',
                            action: () => openFinancialChecklist('getting-started')
                        },
                        ready: isGettingStartedComplete && hasHive('financial'),
                        completed: hasCompletedCareMapStep('started-getting-started')
                    },
                    {
                        heading: 'Start Estate Planning',
                        text: 'Simplify the estate planning with a step-by-step guide to organizing wills and beneficiary information.',
                        todoBtn: {
                            label: 'Start Estate Planning',
                            action: () => openFinancialChecklist('estate-planning')
                        },
                        doneBtn: {
                            label: 'Open Checklist',
                            action: () => openFinancialChecklist('estate-planning')
                        },
                        ready: isGettingStartedComplete && hasHive('financial'),
                        completed: hasCompletedCareMapStep('started-estate-planning')
                    },
                    {
                        heading: 'Start Advance Care Planning',
                        text: 'Prepare for future medical decisions with a guide to directives, proxies, and care preferences.',
                        todoBtn: {
                            label: 'Start Advance Care Planning',
                            action: () => openFinancialChecklist('advance-care')
                        },
                        doneBtn: {
                            label: 'View Advance Care Planning',
                            action: () => openFinancialChecklist('advance-care')
                        },
                        ready: isGettingStartedComplete && hasHive('financial'),
                        completed: hasCompletedCareMapStep('started-advance-care')
                    }
                ]
            }
        ];
    };
}
